import React, { useId } from 'react';

import { Box, garmentPatternShapes, patternOverlayShapes, ShapeNode } from 'shared/kit/garment-shapes';
import { KitPattern } from 'shared/kit/kit-pattern';

export type { Box };

/** Примитивы из `shared/kit/garment-shapes` → элементы React. Геометрия живёт там, здесь только рендер. */
const renderShapes = (nodes: ShapeNode[]): React.ReactElement[] =>
  nodes.map((node, index) =>
    React.createElement(node.tag, { key: index, ...node.attrs }, node.children ? renderShapes(node.children) : undefined),
  );

// ── PatternOverlay (SVG helper) ────────────────────────────────────────────

interface PatternOverlayProps {
  pattern: KitPattern;
  color2: string;
  width: number;
  height: number;
  rx?: number;
}

export const PatternOverlay: React.FC<PatternOverlayProps> = ({ pattern, color2, width, height, rx = 0 }) => {
  // Идентификатор клипа обязан быть уникальным на документ и стабильным между
  // рендерами: со `Math.random()` каждая перерисовка плодила новый `clipPath`.
  const uniqueId = useId().replace(/:/g, '');
  const clipId = `clip-${pattern}-${uniqueId}`;

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={width} height={height} rx={rx} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>{renderShapes(patternOverlayShapes(pattern, { width, height }, color2, `grad-${clipId}`))}</g>
    </>
  );
};

// ── GarmentPattern ─────────────────────────────────────────────────────────

// Generic pattern fill that draws within an arbitrary bounding box (clipped to a garment shape by the caller).
export const GarmentPattern: React.FC<{ pattern: KitPattern; color2: string; box: Box; id: string }> = ({
  pattern,
  color2,
  box,
  id,
}) => <>{renderShapes(garmentPatternShapes(pattern, box, color2, id))}</>;
